import React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography } from '@thingsw/pitta-design-system';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Webviewer } from '@thingsw/pitta-modules';
import { useSelector } from 'react-redux';
import { THEME } from '../features/Theme/slice';
import { RootState } from '../features/store';

export interface INoResultsProps {
  groupCamera?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    margin: theme.spacing(2, 0),
    flexGrow: 1,
  },
  content: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.25),
    [theme.breakpoints.up(Webviewer.mobile)]: {
      marginRight: theme.spacing(0.25),
      marginBottom: 0,
    },
  },
  groupCamera: {
    margin: theme.spacing(1, 0, 0),
    [theme.breakpoints.up(Webviewer.mobile)]: {
      margin: 0,
    },
  },
}));

export const NoResults = (props: INoResultsProps) => {
  const { groupCamera } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const { color, colors } = useSelector((state: RootState) => state[THEME]);

  return (
    <div className={classes.root}>
      {color === 'light' ? (
        <svg
          width='67'
          height='67'
          viewBox='0 0 67 67'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clip-path='url(#clip0_673_430235)'>
            <path
              d='M16.5 27.5H51.5C55.6421 27.5 59 30.8579 59 35V45C59 49.1421 55.6421 52.5 51.5 52.5H16.5C12.3579 52.5 9 49.1421 9 45V35C9 30.8579 12.3579 27.5 16.5 27.5Z'
              stroke='#BEBEC1'
              strokeWidth='5'
            />
            <path
              d='M13.0625 17.5H23.0625C24.4432 17.5 25.5625 18.6193 25.5625 20C25.5625 21.3807 24.4432 22.5 23.0625 22.5H13.0625C11.6818 22.5 10.5625 21.3807 10.5625 20C10.5625 18.6193 11.6818 17.5 13.0625 17.5Z'
              fill='#BEBEC1'
            />
            <path
              d='M41.625 44.75C38.9326 44.75 36.75 42.5674 36.75 39.875C36.75 37.1826 38.9326 35 41.625 35C44.3174 35 46.5 37.1826 46.5 39.875C46.5 42.5674 44.3174 44.75 41.625 44.75Z'
              fill='#68686E'
              stroke='#BEBEC1'
              strokeWidth='3'
            />
            <path
              d='M42.4253 20.8837C44.7155 19.1814 46.4681 17.3491 46.2711 14.8139C46.1151 12.0553 43.8208 10.4058 41.116 10.5561C39.1747 10.7065 37.3893 11.7912 36.6628 13.6926C36.1374 15.0618 34.7994 15.9556 33.3218 16.0531L33.0304 16.0734C30.2477 16.2562 28.2078 13.4692 29.2996 10.9299C31.126 6.68431 35.5095 4.38884 40.7302 4.04757C48.4998 3.53566 54.2582 7.17591 54.7138 13.9851C55.0175 18.5355 52.8422 21.5582 49.3412 23.9755C47.1453 25.4666 45.873 26.8723 45.4297 29.0093C45.089 30.6547 43.6853 31.8817 41.9902 31.9914C39.6097 32.1498 37.693 30.0981 38.0131 27.758C38.4892 24.2315 40.053 22.5779 42.4212 20.8878L42.4253 20.8837Z'
              fill='#68686E'
            />
          </g>
          <defs>
            <clipPath id='clip0_673_430235'>
              <rect width='67' height='67' fill='white' />
            </clipPath>
          </defs>
        </svg>
      ) : (
        <svg
          width='67'
          height='67'
          viewBox='0 0 67 67'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clipPath='url(#clip0_673_430214)'>
            <path
              d='M16.5 27.5H51.5C55.6421 27.5 59 30.8579 59 35V45C59 49.1421 55.6421 52.5 51.5 52.5H16.5C12.3579 52.5 9 49.1421 9 45V35C9 30.8579 12.3579 27.5 16.5 27.5Z'
              stroke='#636367'
              strokeWidth='5'
            />
            <path
              d='M13.0625 17.5H23.0625C24.4432 17.5 25.5625 18.6193 25.5625 20C25.5625 21.3807 24.4432 22.5 23.0625 22.5H13.0625C11.6818 22.5 10.5625 21.3807 10.5625 20C10.5625 18.6193 11.6818 17.5 13.0625 17.5Z'
              fill='#636367'
            />
            <path
              d='M41.625 44.75C38.9326 44.75 36.75 42.5674 36.75 39.875C36.75 37.1826 38.9326 35 41.625 35C44.3174 35 46.5 37.1826 46.5 39.875C46.5 42.5674 44.3174 44.75 41.625 44.75Z'
              fill='#D0D0D5'
              stroke='#636367'
              strokeWidth='3'
            />
            <path
              d='M42.4253 20.8837C44.7155 19.1814 46.4681 17.3491 46.2711 14.8139C46.1151 12.0553 43.8208 10.4058 41.116 10.5561C39.1747 10.7065 37.3893 11.7912 36.6628 13.6926C36.1374 15.0618 34.7994 15.9556 33.3218 16.0531L33.0304 16.0734C30.2477 16.2562 28.2078 13.4692 29.2996 10.9299C31.126 6.68431 35.5095 4.38884 40.7302 4.04757C48.4998 3.53566 54.2582 7.17591 54.7138 13.9851C55.0175 18.5355 52.8422 21.5582 49.3412 23.9755C47.1453 25.4666 45.873 26.8723 45.4297 29.0093C45.089 30.6547 43.6853 31.8817 41.9902 31.9914C39.6097 32.1498 37.693 30.0981 38.0131 27.758C38.4892 24.2315 40.053 22.5779 42.4212 20.8878L42.4253 20.8837Z'
              fill='#D0D0D5'
            />
          </g>
          <defs>
            <clipPath id='clip0_673_430214'>
              <rect width='67' height='67' fill='white' />
            </clipPath>
          </defs>
        </svg>
      )}
      <Typography
        category='Default'
        variant='Small'
        className={clsx(classes.content, groupCamera && classes.groupCamera)}
        htmlColor={colors.primary['2']}
      >
        {t('No results')}
      </Typography>
    </div>
  );
};
